/**
 * @fileoverview Tela "Ordens de serviço" (admin): OS recebidas do sistema do
 * fabricante pelo coletor do posto, com busca, filtros, estado do coletor e o
 * detalhe de cada OS. Nesta fase só registra e mostra; nada fala com o cliente.
 *
 * Com mais de uma fonte (integração) ativa, aparece o filtro por fonte; trocar a
 * fonte limpa o estado e o tipo, porque as opções deles passam a ser as da fonte.
 * A garantia fica: em garantia e fora querem dizer o mesmo em todas as fontes (o
 * servidor lê os códigos de cada uma, como a triagem).
 *
 * O estado da triagem tem cor (os tons do painel da triagem no chat) e uma barra
 * com quantas OS há em cada um, que filtra num clique. A ordem é escolhida no
 * título da coluna (no celular, num seletor) e feita pelo servidor, porque a
 * lista vem em páginas; com servidor que ainda não ordena, os títulos ficam
 * parados.
 *
 * Atualiza sozinha: o socket avisa quando chega OS nova, quando a triagem de uma
 * OS muda ou quando muda o estado do coletor (o aviso não traz dados de cliente;
 * a tela busca de novo), e o estado das fontes é relido a cada minuto para o
 * "sem sinal" aparecer sem evento.
 * Com mais de uma página aberta ("Carregar mais"), a lista não recarrega sozinha
 * (perderia as páginas): aparece um aviso com o botão "Atualizar". O atalho para
 * configurar as fontes (Integrações) pede integrations.manage, com a regra nova
 * ligada.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { CircularProgress, MenuItem, useMediaQuery } from "@material-ui/core";
import { AlertTriangle, ChevronDown, ChevronUp, ClipboardList, Search, Settings2 } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import ModernSelect from "../../components/ModernSelect";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import usePermission from "../../hooks/usePermission";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import WorkOrderDetailDrawer from "./WorkOrderDetailDrawer";
import TriageStateBar from "./TriageStateBar";
import { triageChipClass, triageStateLabel, triageStateOf } from "./triageState";
import useStyles from "./styles";
import { EMPTY, formatLocalDateTime } from "./format";

const PAGE_SIZE = 25;
const SOURCES_REFRESH_MS = 60 * 1000;
// Sem escolha: as que chegaram por último primeiro (como era antes de ordenar).
const DEFAULT_SORT = { field: "received", dir: "desc" };
// Ordens do seletor do celular (lá não há título de coluna para clicar).
const MOBILE_SORTS = ["received:desc", "received:asc", "number:asc", "customer:asc", "city:asc", "product:asc", "triage:asc"];

const t = (key, options) => i18n.t(`workOrders.${key}`, options);

/** Selo do estado da triagem da OS (quem chama mostra o traço quando não há triagem). */
const TriageChip = ({ classes, triage, prefix }) => {
  const state = triageStateOf(triage);
  // O motivo do início automático só explica a OS que ainda não tem triagem.
  const reason = state === "not_started" || state === "scheduled" ? triage?.lastError : null;
  const label = triageStateLabel(state);
  return (
    <span className={triageChipClass(classes, state)} title={reason || undefined}>
      {prefix ? `${prefix}: ${label}` : label}
    </span>
  );
};

/** Título de coluna que ordena: o primeiro clique escolhe a coluna, o seguinte inverte. */
const SortableTh = ({ classes, field, sort, onSort }) => {
  const active = sort.field === field;
  const Icon = active && sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th className={classes.th} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`${classes.thBtn} ${active ? classes.thBtnOn : ""}`} onClick={() => onSort(field)}>
        {t(`columns.${field}`)}
        <Icon size={13} strokeWidth={2.5} className={classes.thIcon} aria-hidden="true" />
      </button>
    </th>
  );
};

const SourceStatus = ({ classes, source }) => {
  const params = {
    name: source.name,
    time: formatRelativeTime(source.lastSyncAt),
    error: source.lastSyncError || "",
  };
  if (source.health === "online") {
    return (
      <div className={classes.sourceLine}>
        <span className={classes.dotOk} />
        <span>
          {t("source.online", params)}
          {source.collector?.version ? ` · v${source.collector.version}` : ""}
        </span>
      </div>
    );
  }
  if (source.health === "inactive") {
    return (
      <div className={classes.sourceLine}>
        <span className={classes.dotOff} />
        <span>{t("source.inactive", params)}</span>
      </div>
    );
  }
  const tone =
    source.health === "offline" || source.health === "error" ? classes.bannerError : classes.bannerWarn;
  return (
    <div className={`${classes.banner} ${tone}`} role="alert">
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{t(`source.${source.health}`, params)}</span>
    </div>
  );
};

/** Nome da fonte com o ponto do estado do coletor: verde online, vermelho nos demais. */
const SourceOption = ({ classes, source }) => {
  const health = source.health || "never";
  return (
    <span className={classes.sourceOption}>
      <span
        className={health === "online" ? classes.dotOk : classes.dotError}
        role="img"
        aria-label={t(`sourceHealth.${health}`)}
        title={t(`sourceHealth.${health}`)}
      />
      {source.isActive ? source.name : t("inactiveSource", { name: source.name })}
    </span>
  );
};

const WorkOrders = () => {
  const classes = useStyles();
  const history = useHistory();
  const canConfigure = usePermission().can("integrations.manage", true);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId;
  const socketManager = useContext(SocketContext);

  const [sources, setSources] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  // triage: quantas OS em cada estado da triagem (null com servidor que não conta).
  const [facets, setFacets] = useState({ externalStatus: [], woType: [], warranty: [], triage: null });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [integrationId, setIntegrationId] = useState("");
  const [status, setStatus] = useState("");
  const [woType, setWoType] = useState("");
  const [warranty, setWarranty] = useState("");
  const [triageState, setTriageState] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [selectedId, setSelectedId] = useState(null);
  const [pendingUpdates, setPendingUpdates] = useState(false);
  // O servidor ordena (devolve a ordem aplicada); sem isso, os títulos não ordenam.
  const [canSort, setCanSort] = useState(false);
  // Ignora respostas de buscas antigas (filtro trocado no meio do caminho).
  const requestSeq = useRef(0);
  const pageRef = useRef(1);
  // "Carregar mais" a caminho: o aviso do socket não recarrega (jogaria a página fora).
  const loadingMoreRef = useRef(false);
  // Último estado conhecido de cada triagem (da lista e dos avisos): aviso que só
  // traz progresso, com a triagem no mesmo estado, não recarrega a lista.
  const triageStatusById = useRef(new Map());

  const fetchSources = useCallback(async () => {
    try {
      const { data } = await api.get("/work-orders/sources");
      setSources(data.sources || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchPage = useCallback(
    // silent: recarga pelo socket, sem o indicador (a tabela fica e não perde a rolagem).
    async (pageNumber, { silent = false } = {}) => {
      const seq = ++requestSeq.current;
      if (pageNumber > 1) {
        setLoadingMore(true);
        loadingMoreRef.current = true;
      } else if (!silent) {
        setLoading(true);
      }
      try {
        const { data } = await api.get("/work-orders", {
          params: {
            search: debouncedSearch || undefined,
            integrationId: integrationId || undefined,
            externalStatus: status || undefined,
            woType: woType || undefined,
            warranty: warranty || undefined,
            triage: triageState || undefined,
            sort: sort.field,
            dir: sort.dir,
            page: pageNumber,
            pageSize: PAGE_SIZE,
          },
        });
        if (seq !== requestSeq.current) return;
        setRows((prev) => {
          if (pageNumber === 1) return data.workOrders;
          // Sem repetir OS que a página anterior já trouxe (a lista pode ter mudado).
          const known = new Set(prev.map((w) => w.id));
          return [...prev, ...data.workOrders.filter((w) => !known.has(w.id))];
        });
        setCount(data.count);
        setHasMore(data.hasMore);
        setCanSort(!!data.sort);
        data.workOrders.forEach((w) => {
          if (w.triage?.sessionId) triageStatusById.current.set(w.triage.sessionId, w.triage.status);
        });
        // Só a primeira página traz os filtros; as seguintes mantêm os atuais.
        if (data.facets) {
          setFacets({ ...data.facets, warranty: data.facets.warranty || [], triage: data.facets.triage || null });
        }
        setPage(pageNumber);
        pageRef.current = pageNumber;
        if (pageNumber === 1) setPendingUpdates(false);
      } catch (err) {
        if (seq === requestSeq.current) toastError(err);
      } finally {
        if (pageNumber > 1) loadingMoreRef.current = false;
        if (seq === requestSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [debouncedSearch, integrationId, status, woType, warranty, triageState, sort]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  useEffect(() => {
    fetchSources();
    const timer = setInterval(fetchSources, SOURCES_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchSources]);

  // Socket: OS nova/alterada ou triagem de OS que mudou recarrega a primeira
  // página; sinal do coletor, as fontes.
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  useEffect(() => {
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);
    const workOrderEvent = `company-${companyId}-workOrder`;
    const triageEvent = `company-${companyId}-triage`;
    const sourceEvent = `company-${companyId}-workOrderSource`;
    let timer = null;
    // Com mais páginas abertas (ou uma a caminho), recarregar perderia as páginas: só avisa.
    const keepPages = () => {
      if (pageRef.current <= 1 && !loadingMoreRef.current) return false;
      setPendingUpdates(true);
      return true;
    };
    // Uma recarga por rajada de avisos: o primeiro agenda, os seguintes entram nela.
    const onWorkOrder = () => {
      if (keepPages() || timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (!keepPages()) fetchPageRef.current(1, { silent: true });
      }, 800);
    };
    // Triagens sem OS (manual, pelo fluxo) não mudam esta tela; nem aviso só de progresso.
    const onTriage = (data) => {
      if (!data?.workOrderId || triageStatusById.current.get(data.sessionId) === data.status) return;
      triageStatusById.current.set(data.sessionId, data.status);
      onWorkOrder();
    };
    socket.on(workOrderEvent, onWorkOrder);
    socket.on(triageEvent, onTriage);
    socket.on(sourceEvent, fetchSources);
    return () => {
      clearTimeout(timer);
      socket.off(workOrderEvent, onWorkOrder);
      socket.off(triageEvent, onTriage);
      socket.off(sourceEvent, fetchSources);
    };
  }, [socketManager, companyId, fetchSources]);

  const closeDetail = useCallback(() => setSelectedId(null), []);
  const filtering = !!(debouncedSearch || integrationId || status || woType || warranty || triageState);
  // As garantias que as OS têm, na ordem do servidor ("unknown": sem a informação),
  // e a escolhida, mesmo que tenha sumido. Origem que não informa a garantia
  // (nenhuma OS em garantia nem fora): sem o filtro.
  const warrantyOptions =
    warranty && !facets.warranty.includes(warranty) ? [...facets.warranty, warranty] : facets.warranty;
  const showWarrantyFilter = !!warranty || facets.warranty.some((w) => w !== "unknown");
  // A barra conta as OS dos outros filtros; sem nenhuma, fica só a mensagem de vazio.
  const showStateBar = Array.isArray(facets.triage) && (facets.triage.length > 0 || !!triageState);
  const changeSort = (field) =>
    setSort((current) =>
      current.field === field
        ? { field, dir: current.dir === "asc" ? "desc" : "asc" }
        : { field, dir: field === DEFAULT_SORT.field ? DEFAULT_SORT.dir : "asc" }
    );
  const sortableTh = (field) =>
    canSort ? (
      <SortableTh classes={classes} field={field} sort={sort} onSort={changeSort} />
    ) : (
      <th className={classes.th}>{t(`columns.${field}`)}</th>
    );
  const sortValue = `${sort.field}:${sort.dir}`;
  // A ordem escolhida no computador continua no seletor do celular, mesmo fora da lista curta.
  const mobileSorts = MOBILE_SORTS.includes(sortValue) ? MOBILE_SORTS : [...MOBILE_SORTS, sortValue];
  const noSources = sources !== null && sources.length === 0;
  const showSourceFilter = (sources || []).filter((s) => s.isActive).length > 1;

  // Sem o filtro (sobrou uma fonte ativa), a escolha antiga não pode ficar valendo escondida.
  useEffect(() => {
    if (sources && !showSourceFilter && integrationId) setIntegrationId("");
  }, [sources, showSourceFilter, integrationId]);

  const changeSource = (value) => {
    setIntegrationId(value);
    setStatus("");
    setWoType("");
  };

  return (
    <MainContainer>
      <div className={classes.page}>
        <div className={classes.header}>
          <div>
            <div className={classes.title}>
              <ClipboardList size={22} />
              {t("title")}
            </div>
            <div className={classes.subtitle}>{t("subtitle")}</div>
          </div>
          {canConfigure && (
            <button type="button" className={classes.linkBtn} onClick={() => history.push("/integrations")}>
              <Settings2 size={14} /> {t("configure")}
            </button>
          )}
        </div>

        {(sources || []).map((source) => (
          <SourceStatus key={source.id} classes={classes} source={source} />
        ))}
        {noSources && (
          <div className={`${classes.banner} ${classes.bannerNeutral}`}>{t("empty.noSources")}</div>
        )}

        <div className={classes.filters}>
          <div className={classes.searchWrap}>
            <Search size={14} className={classes.searchIcon} />
            <input
              type="search"
              className={classes.input}
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t("search")}
            />
          </div>
          {showSourceFilter && (
            <ModernSelect
              size="small"
              className={classes.filterSelect}
              displayEmpty
              value={integrationId}
              onChange={(e) => changeSource(e.target.value)}
              inputProps={{ "aria-label": t("sourceFilter") }}
              renderValue={(value) => {
                const source = sources.find((s) => s.id === value);
                return source ? <SourceOption classes={classes} source={source} /> : t("allSources");
              }}
            >
              <MenuItem value="">{t("allSources")}</MenuItem>
              {sources.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <SourceOption classes={classes} source={s} />
                </MenuItem>
              ))}
            </ModernSelect>
          )}
          <ModernSelect
            size="small"
            className={classes.filterSelect}
            displayEmpty
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            inputProps={{ "aria-label": t("columns.status") }}
          >
            <MenuItem value="">{t("allStatuses")}</MenuItem>
            {facets.externalStatus.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </ModernSelect>
          <ModernSelect
            size="small"
            className={classes.filterSelect}
            displayEmpty
            value={woType}
            onChange={(e) => setWoType(e.target.value)}
            inputProps={{ "aria-label": t("columns.type") }}
          >
            <MenuItem value="">{t("allTypes")}</MenuItem>
            {facets.woType.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </ModernSelect>
          {showWarrantyFilter && (
            <ModernSelect
              size="small"
              className={classes.filterSelect}
              displayEmpty
              value={warranty}
              onChange={(e) => setWarranty(e.target.value)}
              inputProps={{ "aria-label": t("columns.warranty") }}
            >
              <MenuItem value="">{t("allWarranties")}</MenuItem>
              {warrantyOptions.map((w) => (
                <MenuItem key={w} value={w}>
                  {t(`warrantyFilter.${w}`)}
                </MenuItem>
              ))}
            </ModernSelect>
          )}
          {isMobile && canSort && (
            <ModernSelect
              size="small"
              className={classes.filterSelect}
              value={sortValue}
              onChange={(e) => {
                const [field, dir] = e.target.value.split(":");
                setSort({ field, dir });
              }}
              inputProps={{ "aria-label": t("sort.label") }}
            >
              {mobileSorts.map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`sort.options.${v.replace(":", "_")}`)}
                </MenuItem>
              ))}
            </ModernSelect>
          )}
          {/* Com a barra da triagem, o total já está nela ("Todas"). */}
          {!loading && !showStateBar && <span className={classes.total}>{t("total", { count })}</span>}
        </div>

        {showStateBar && (
          <TriageStateBar classes={classes} counts={facets.triage} value={triageState} onChange={setTriageState} />
        )}

        {pendingUpdates && !loading && (
          <div className={`${classes.banner} ${classes.bannerNeutral} ${classes.bannerSticky}`} role="status">
            <span style={{ flex: 1 }}>{t("updatesAvailable")}</span>
            <button type="button" className={classes.linkBtn} onClick={() => fetchPage(1)}>
              {t("refresh")}
            </button>
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <CircularProgress size={28} />
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className={classes.empty}>
            {filtering ? (
              t("empty.filtered")
            ) : (
              <>
                <ClipboardList size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                <div className={classes.emptyTitle}>{t("empty.title")}</div>
                <div>{t("empty.description")}</div>
              </>
            )}
          </div>
        )}

        {!loading && rows.length > 0 && isMobile && (
          <div className={classes.cardList}>
            {rows.map((wo) => (
              <button key={wo.id} type="button" className={classes.mCard} onClick={() => setSelectedId(wo.id)}>
                <div className={classes.mCardTop}>
                  <span className={classes.osNumber}>{wo.externalId}</span>
                  {wo.externalStatusLabel && <span className={classes.chip}>{wo.externalStatusLabel}</span>}
                </div>
                <div className={classes.mCardName}>{wo.customerName || EMPTY}</div>
                {triageStateOf(wo.triage) !== "none" && (
                  <div style={{ marginBottom: 4 }}>
                    <TriageChip classes={classes} triage={wo.triage} prefix={t("columns.triage")} />
                  </div>
                )}
                <div className={classes.tdMuted}>
                  {[
                    [wo.woTypeLabel, wo.woSubType].filter(Boolean).join(" / "),
                    wo.warrantyStatusLabel,
                    [wo.city, wo.state].filter(Boolean).join("/"),
                  ]
                    .filter(Boolean)
                    .join(" · ") || EMPTY}
                </div>
                <div className={classes.tdMuted}>{formatLocalDateTime(wo.receivedAt)}</div>
              </button>
            ))}
          </div>
        )}

        {!loading && rows.length > 0 && !isMobile && (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  {sortableTh("number")}
                  {sortableTh("received")}
                  <th className={classes.th}>{t("columns.status")}</th>
                  <th className={classes.th}>{t("columns.type")}</th>
                  <th className={classes.th}>{t("columns.warranty")}</th>
                  {sortableTh("customer")}
                  {sortableTh("city")}
                  {sortableTh("product")}
                  {sortableTh("triage")}
                </tr>
              </thead>
              <tbody>
                {rows.map((wo) => (
                  <tr
                    key={wo.id}
                    className={classes.tr}
                    tabIndex={0}
                    onClick={() => setSelectedId(wo.id)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSelectedId(wo.id)}
                  >
                    <td className={`${classes.td} ${classes.osNumber}`}>{wo.externalId}</td>
                    <td className={`${classes.td} ${classes.tdMuted}`}>{formatLocalDateTime(wo.receivedAt)}</td>
                    <td className={classes.td}>
                      {wo.externalStatusLabel ? <span className={classes.chip}>{wo.externalStatusLabel}</span> : EMPTY}
                    </td>
                    <td className={`${classes.td} ${classes.tdMuted}`}>
                      {[wo.woTypeLabel, wo.woSubType].filter(Boolean).join(" / ") || EMPTY}
                    </td>
                    <td className={`${classes.td} ${classes.tdMuted}`}>{wo.warrantyStatusLabel || EMPTY}</td>
                    <td className={classes.td}>{wo.customerName || EMPTY}</td>
                    <td className={`${classes.td} ${classes.tdMuted}`}>
                      {[wo.city, wo.state].filter(Boolean).join("/") || EMPTY}
                    </td>
                    <td className={`${classes.td} ${classes.tdMuted}`}>
                      {[wo.productBrand, wo.productModel].filter(Boolean).join(" ") || EMPTY}
                    </td>
                    <td className={classes.td}>
                      {triageStateOf(wo.triage) === "none" ? EMPTY : <TriageChip classes={classes} triage={wo.triage} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && hasMore && (
          <div className={classes.loadMore}>
            <button
              type="button"
              className={classes.linkBtn}
              disabled={loadingMore}
              onClick={() => fetchPage(page + 1)}
            >
              {loadingMore ? <CircularProgress size={14} /> : t("loadMore")}
            </button>
          </div>
        )}

        <WorkOrderDetailDrawer workOrderId={selectedId} onClose={closeDetail} />
      </div>
    </MainContainer>
  );
};

export default WorkOrders;
